export type AskAiProvider = 'openai' | 'gemini' | 'anthropic'

export interface AskAiProviderConfig {
  provider: AskAiProvider
  apiKey: string
  model?: string
}

interface AskAiStoredMulti {
  version: 2
  activeProvider: AskAiProvider
  byProvider: Partial<
    Record<AskAiProvider, { apiKey: string; model?: string }>
  >
}

const STORAGE_KEY = 'm2mba:ask-ai-config'

export const ASK_AI_DEFAULT_MODELS: Record<AskAiProvider, string> = {
  openai: 'gpt-4o',
  gemini: 'gemini-2.0-flash',
  anthropic: 'claude-sonnet-4-20250514',
}

export const ASK_AI_PROVIDER_LABELS: Record<AskAiProvider, string> = {
  openai: 'OpenAI',
  gemini: 'Gemini',
  anthropic: 'Claude',
}

export const ASK_AI_PROVIDERS: AskAiProvider[] = ['openai', 'gemini', 'anthropic']

function isProvider(value: unknown): value is AskAiProvider {
  return value === 'openai' || value === 'gemini' || value === 'anthropic'
}

function readRaw(): unknown | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

function toMulti(raw: unknown): AskAiStoredMulti | null {
  if (!raw || typeof raw !== 'object') return null
  const obj = raw as Record<string, unknown>

  // v2
  if (obj.version === 2 && isProvider(obj.activeProvider) && obj.byProvider && typeof obj.byProvider === 'object') {
    const byProvider: AskAiStoredMulti['byProvider'] = {}
    for (const p of ASK_AI_PROVIDERS) {
      const entry = (obj.byProvider as Record<string, unknown>)[p]
      if (entry && typeof entry === 'object') {
        const apiKey = String((entry as { apiKey?: unknown }).apiKey || '').trim()
        if (apiKey.length >= 10) {
          const model = (entry as { model?: unknown }).model
          byProvider[p] = {
            apiKey,
            model: model ? String(model) : undefined,
          }
        }
      }
    }
    return {
      version: 2,
      activeProvider: obj.activeProvider,
      byProvider,
    }
  }

  // v1: { provider, apiKey, model? }
  if (isProvider(obj.provider) && obj.apiKey) {
    const apiKey = String(obj.apiKey).trim()
    if (apiKey.length < 10) return null
    return {
      version: 2,
      activeProvider: obj.provider,
      byProvider: {
        [obj.provider]: {
          apiKey,
          model: obj.model ? String(obj.model) : undefined,
        },
      },
    }
  }

  return null
}

function writeMulti(multi: AskAiStoredMulti): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(multi))
}

export function loadAskAiConfig(): AskAiProviderConfig | null {
  const multi = toMulti(readRaw())
  if (!multi) return null
  const entry = multi.byProvider[multi.activeProvider]
  if (!entry?.apiKey || entry.apiKey.length < 10) return null
  return {
    provider: multi.activeProvider,
    apiKey: entry.apiKey,
    model: entry.model,
  }
}

export function loadAskAiConfigFor(provider: AskAiProvider): AskAiProviderConfig | null {
  const multi = toMulti(readRaw())
  const entry = multi?.byProvider[provider]
  if (!entry?.apiKey || entry.apiKey.length < 10) return null
  return {
    provider,
    apiKey: entry.apiKey,
    model: entry.model,
  }
}

export function saveAskAiConfig(config: AskAiProviderConfig): void {
  const existing = toMulti(readRaw()) || {
    version: 2 as const,
    activeProvider: config.provider,
    byProvider: {},
  }
  const apiKey = config.apiKey.trim()
  existing.activeProvider = config.provider
  if (apiKey.length >= 10) {
    existing.byProvider[config.provider] = {
      apiKey,
      model: config.model?.trim() || undefined,
    }
  }
  writeMulti(existing)
}

/** Đổi provider đang dùng. Trả về config nếu đã có key; null nếu chưa cấu hình key cho provider đó. */
export function switchAskAiProvider(provider: AskAiProvider): AskAiProviderConfig | null {
  const existing = toMulti(readRaw())
  if (!existing) return null
  const entry = existing.byProvider[provider]
  if (!entry?.apiKey || entry.apiKey.length < 10) return null
  existing.activeProvider = provider
  writeMulti(existing)
  return {
    provider,
    apiKey: entry.apiKey,
    model: entry.model,
  }
}

export function hasAskAiKeyFor(provider: AskAiProvider): boolean {
  const multi = toMulti(readRaw())
  const key = multi?.byProvider[provider]?.apiKey
  return !!(key && key.length >= 10)
}

/** Xóa toàn bộ cấu hình Ask AI (mọi provider). */
export function clearAskAiConfig(): void {
  localStorage.removeItem(STORAGE_KEY)
}

/**
 * Xóa API key của một provider. Giữ key các provider khác.
 * Nếu đang active provider bị xóa → chuyển sang provider còn key (nếu có).
 */
export function clearAskAiProvider(provider: AskAiProvider): void {
  const existing = toMulti(readRaw())
  if (!existing) return

  const nextByProvider = { ...existing.byProvider }
  delete nextByProvider[provider]

  const remaining = ASK_AI_PROVIDERS.filter((p) => {
    const key = nextByProvider[p]?.apiKey
    return !!(key && key.length >= 10)
  })

  if (remaining.length === 0) {
    localStorage.removeItem(STORAGE_KEY)
    return
  }

  const activeProvider =
    existing.activeProvider === provider ? remaining[0] : existing.activeProvider

  writeMulti({
    version: 2,
    activeProvider,
    byProvider: nextByProvider,
  })
}

export function hasAskAiConfig(): boolean {
  const cfg = loadAskAiConfig()
  return !!(cfg?.apiKey && cfg.apiKey.length >= 10)
}

export function getAskAiGuide(provider: AskAiProvider): {
  title: string
  steps: string[]
  url: string
} {
  switch (provider) {
    case 'openai':
      return {
        title: 'Lấy API key OpenAI',
        url: 'https://platform.openai.com/api-keys',
        steps: [
          'Đăng nhập OpenAI Platform (platform.openai.com).',
          'Vào mục API keys → Create new secret key.',
          'Sao chép key (chỉ hiện 1 lần) và dán vào ô bên dưới.',
          'Cần có credit / billing trên tài khoản OpenAI để gọi model.',
        ],
      }
    case 'gemini':
      return {
        title: 'Lấy API key Google Gemini (có free tier)',
        url: 'https://aistudio.google.com/apikey',
        steps: [
          'Mở Google AI Studio (aistudio.google.com).',
          'Chọn Get API key / Create API key.',
          'Sao chép key và dán vào ô bên dưới.',
          'Dùng model Gemini Flash (mặc định) — có hạn mức miễn phí.',
        ],
      }
    case 'anthropic':
      return {
        title: 'Lấy API key Anthropic Claude',
        url: 'https://console.anthropic.com/settings/keys',
        steps: [
          'Đăng nhập Anthropic Console.',
          'Vào Settings → API Keys → Create Key.',
          'Sao chép key và dán vào ô bên dưới.',
          'Tài khoản cần có credit để gọi Claude.',
        ],
      }
  }
}
